import { Card, CardContent, Divider, Skeleton, Tab, Tabs } from '@mui/material'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useIntegrations } from '@/api/hooks'
import { ErrorBanner, PageHeader } from '@/components/common'
import { EInvoiceTab } from '@/pages/settings/EInvoiceTab'
import { IntegrationCard } from '@/pages/settings/IntegrationCard'

const TAB_KEYS = ['email', 'whatsapp', 'sms', 'e-invoice', 'gstin'] as const

/**
 * The outside world, in one place.
 *
 * <p>Everything this system talks to that is not itself: the mail server, the
 * WhatsApp account, the SMS gateway and the e-invoice portal. They were
 * scattered - one in an environment variable a deployment away from the person
 * who knows the password, one buried in a settings tab - and they are the same
 * kind of thing, set up once by whoever holds the accounts.</p>
 */
export function ApiConfigurationPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const initial = Math.max(
    TAB_KEYS.indexOf((requested ?? 'email') as (typeof TAB_KEYS)[number]),
    0,
  )
  const [tab, setTab] = useState(initial)
  const { data, error, isLoading } = useIntegrations()

  const accountFor = (provider: string) =>
    data?.find((account) => account.provider === provider)

  return (
    <>
      <PageHeader
        title="API configuration"
        subtitle="The accounts this business sends through, and whether each one works"
      />
      <ErrorBanner error={error} />
      <Card>
        <Tabs
          value={tab}
          onChange={(_event, value) => {
            setTab(value)
            setSearchParams({ tab: TAB_KEYS[value] })
          }}
          variant="scrollable"
        >
          <Tab label="Email" />
          <Tab label="WhatsApp" />
          <Tab label="SMS" />
          <Tab label="E-invoice" />
          <Tab label="GST verification" />
        </Tabs>
        <Divider />
        <CardContent>
          {isLoading && tab < 3 ? <Skeleton height={280} /> : null}
          {tab === 0 && accountFor('EMAIL') ? (
            <IntegrationCard account={accountFor('EMAIL')!} />
          ) : null}
          {tab === 1 && accountFor('WHATSAPP') ? (
            <IntegrationCard account={accountFor('WHATSAPP')!} />
          ) : null}
          {tab === 2 && accountFor('SMS') ? (
            <IntegrationCard account={accountFor('SMS')!} />
          ) : null}
          {/* Filing is not a message, but it is the same question: which outside
              service, under whose account. It lived under Settings and belongs
              here beside the rest. */}
          {tab === 3 && accountFor('EINVOICE') ? (
            <IntegrationCard account={accountFor('EINVOICE')!} />
          ) : null}
          {tab === 3 && <EInvoiceTab />}
          {/* Neither a message nor a filing, but still an outside account held
              against this business's own name - a lookup rather than a send,
              tested by looking a GSTIN up instead of writing to one. */}
          {tab === 4 && accountFor('GSTIN') ? (
            <IntegrationCard account={accountFor('GSTIN')!} />
          ) : null}
        </CardContent>
      </Card>
    </>
  )
}
